import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { Play, ShieldAlert, TriangleAlert, Wrench } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type {
  LedgerBalanceDriftAlertRow,
  LedgerDriftCheckResult,
  LedgerDriftResolution,
} from '@/api/ledger-types'
import { session } from '@/api/session'
import { ProblemAlert } from '@/components/error-surface'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { branchDescription } from '@/lib/branch'
import { formatDateTime, formatShortDate } from '@/lib/datetime'

import {
  DRIFT_RESOLUTION_LABEL,
  DRIFT_RESOLUTION_MEANING,
  DRIFT_RESOLUTION_TONE,
  DRIFT_TRIGGER_LABEL,
  startOfMonth,
  today,
} from './constants'
import { useResolveDriftAlert, useRunDriftCheck } from './mutations'
import { LEDGER_PERMISSIONS, useLedgerPermissions } from './permissions'
import { useDriftAlerts, useLedgerBranchReach } from './queries'

/**
 * The balance drift register.
 *
 * An integrity check re-derives every stored daily rollup from the postings underneath it. Where the
 * two disagree it raises an alarm, and **an unresolved alarm means a reported figure may be wrong**.
 * That is not a warning: the rollups are what the trial balance is built from and what the Bank of
 * Ghana return is built from, so a rollup that does not follow from its postings is a number the
 * institution is publishing without being able to derive it.
 *
 * An unresolved alarm also **blocks the branch's day close**, which is the mechanism that stops the
 * bad figure being carried forward and reported. So this register is not a monitoring screen anybody
 * may get round to; it is a queue with a deadline at the end of every trading day.
 *
 * The size of a difference says nothing about the size of the fault behind it. A rollup out by one
 * pesewa is a rollup that was not derived from the postings, and the reason for that is what matters
 * — never the magnitude.
 *
 * Read-only at any width. The disposition is a dialog: nothing is keyed here but a reason.
 */

/** The endpoint orders its own rows — unresolved first, which is the order the work is done in. */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const RESOLUTIONS: LedgerDriftResolution[] = ['Unresolved', 'Repaired', 'AcceptedWithExplanation']

const ANY = '__any__'

const DEFAULT_PAGE_SIZE = 50

export function BalanceDriftSection() {
  const permissions = useLedgerPermissions()
  const reach = useLedgerBranchReach()

  const [branchId, setBranchId] = useState<string | null>(() => session.getBranchId())
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [resolution, setResolution] = useState<LedgerDriftResolution | null>('Unresolved')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [resolving, setResolving] = useState<LedgerBalanceDriftAlertRow | null>(null)

  const query = useDriftAlerts(
    { branchId, fromDate, toDate, resolution, page, pageSize },
    permissions.canViewDrift,
  )

  /*
   * Memoised, and not merely for speed: the table is given its columns as state, and handing it a
   * fresh array on every render makes it rebuild its model each time a filter changes — which
   * discards the row identity that keeps focus where it was.
   */
  const onResolve = useCallback((alert: LedgerBalanceDriftAlertRow) => setResolving(alert), [])
  const columns: DataTableColumn<LedgerBalanceDriftAlertRow>[] = useMemo(
    () => buildColumns(onResolve),
    [onResolve],
  )

  if (!permissions.canViewDrift) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
        <ShieldAlert className="size-6 text-warning-text" aria-hidden />
        <p className="text-sm font-medium text-heading">The drift register is restricted</p>
        <p className="max-w-lg text-xs text-muted">
          Reading it needs <span className="font-mono">{LEDGER_PERMISSIONS.driftView}</span>, which
          your role does not hold. A day close still checks for drift and still refuses over one — the
          refusal will name it even though the register is closed to you.
        </p>
      </div>
    )
  }

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater

    setPage(next.pageIndex + 1)
    setPageSize(next.pageSize)
  }

  const unresolvedShown =
    resolution === 'Unresolved' ? (query.data?.totalCount ?? 0) : null

  return (
    <div className="space-y-4">
      <Alert tone={unresolvedShown !== null && unresolvedShown > 0 ? 'danger' : 'info'}>
        <TriangleAlert aria-hidden />
        <div className="space-y-1">
          <AlertTitle>
            {unresolvedShown !== null && unresolvedShown > 0
              ? `${String(unresolvedShown)} unresolved alarm${unresolvedShown === 1 ? '' : 's'}`
              : 'What an alarm means'}
          </AlertTitle>
          <AlertDescription>
            A stored daily balance did not agree with the postings underneath it. Until the alarm is
            disposed of, a reported figure may be wrong — and the branch&apos;s day will not close,
            which is the control that stops the figure being carried forward. The size of the
            difference is not the measure of the problem: a rollup out by one pesewa is a rollup that
            was not derived from its postings.
          </AlertDescription>
        </div>
      </Alert>

      <RunDriftCheckCard defaultBranchId={branchId} />

      <div className="grid gap-3 rounded-card border border-border-subtle bg-surface p-3 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="drift-branch">Branch</Label>
          <Select
            value={branchId ?? ANY}
            onValueChange={(next) => {
              setBranchId(next === ANY ? null : next)
              setPage(1)
            }}
          >
            <SelectTrigger id="drift-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every branch you can reach</SelectItem>
              {reach.branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branchDescription(branch)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="drift-from">From</Label>
          <Input
            id="drift-from"
            type="date"
            value={fromDate}
            onChange={(event) => {
              setFromDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="drift-to">To</Label>
          <Input
            id="drift-to"
            type="date"
            value={toDate}
            onChange={(event) => {
              setToDate(event.target.value)
              setPage(1)
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="drift-resolution">Disposition</Label>
          <Select
            value={resolution ?? ANY}
            onValueChange={(next) => {
              setResolution(next === ANY ? null : (next as LedgerDriftResolution))
              setPage(1)
            }}
          >
            <SelectTrigger id="drift-resolution">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every alarm</SelectItem>
              {RESOLUTIONS.map((candidate) => (
                <SelectItem key={candidate} value={candidate}>
                  {DRIFT_RESOLUTION_LABEL[candidate]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={query.data?.items}
        rowCount={query.data?.totalCount}
        pagination={{ pageIndex: page - 1, pageSize }}
        onPaginationChange={onPaginationChange}
        sorting={UNSORTED}
        onSortingChange={ignoreSortingChange}
        isLoading={query.isPending}
        isFetching={query.isFetching}
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
        getRowId={(row) => row.publicId}
        onRowActivate={(row) => setResolving(row)}
        pageSizeOptions={[25, DEFAULT_PAGE_SIZE, 100]}
        emptyTitle={resolution === 'Unresolved' ? 'Nothing unresolved' : 'No alarms'}
        emptyDescription={
          resolution === 'Unresolved'
            ? 'Every stored rollup in this range agrees with the postings underneath it, or every disagreement has been disposed of. Nothing is blocking a day close on drift.'
            : 'No alarm has been raised for those filters. That is the nightly check finding nothing, which is what it should usually find.'
        }
      />

      {resolving ? (
        <ResolveDriftDialog alert={resolving} onClose={() => setResolving(null)} />
      ) : null}
    </div>
  )
}

const helper = dataTableColumns<LedgerBalanceDriftAlertRow>()

function buildColumns(
  onResolve: (alert: LedgerBalanceDriftAlertRow) => void,
): DataTableColumn<LedgerBalanceDriftAlertRow>[] {
  return helper.columns([
    helper.accessor('subject', {
      header: 'Subject',
      enableSorting: false,
      cell: (context) => (
        <>
          <span className="text-sm font-medium text-heading">{context.row.original.subject}</span>
          <p className="font-mono text-2xs text-muted">{context.row.original.subjectKey}</p>
        </>
      ),
      meta: { card: 'title' },
    }),
    helper.accessor('businessDate', {
      header: 'Business date',
      enableSorting: false,
      // Null on a subject that is not dated, which is a different fact from an unknown date.
      cell: (context) =>
        context.row.original.businessDate === null ? (
          <span className="text-xs text-muted">Not dated</span>
        ) : (
          <span className="numeric text-xs tabular-nums">
            {formatShortDate(context.row.original.businessDate)}
          </span>
        ),
      meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
    }),
    helper.accessor('branchId', {
      header: 'Branch',
      enableSorting: false,
      cell: (context) =>
        context.row.original.branchId === null ? (
          <span className="text-xs text-muted">Institution</span>
        ) : (
          <span className="font-mono text-xs">Branch {context.row.original.branchId}</span>
        ),
      meta: { noWrap: true, widthClass: 'w-28' },
    }),
    helper.accessor('storedMinorUnits', {
      header: 'Stored',
      enableSorting: false,
      cell: (context) => <MoneyDisplay value={context.row.original.storedMinorUnits} symbol={false} />,
      meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Stored' },
    }),
    helper.accessor('derivedMinorUnits', {
      header: 'Derived',
      enableSorting: false,
      cell: (context) => <MoneyDisplay value={context.row.original.derivedMinorUnits} symbol={false} />,
      meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Derived' },
    }),
    helper.accessor('differenceMinorUnits', {
      header: 'Difference',
      enableSorting: false,
      cell: (context) => (
        <MoneyDisplay
          value={context.row.original.differenceMinorUnits}
          symbol={false}
          signed
          colourNegative
          emphasis="strong"
        />
      ),
      meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Difference' },
    }),
    helper.accessor('resolution', {
      header: 'Disposition',
      enableSorting: false,
      cell: (context) => (
        <Badge tone={DRIFT_RESOLUTION_TONE[context.row.original.resolution]}>
          {DRIFT_RESOLUTION_LABEL[context.row.original.resolution]}
        </Badge>
      ),
      meta: { widthClass: 'w-40', card: 'lead' },
    }),
    helper.display({
      id: 'resolve',
      header: 'Action',
      cell: (context) =>
        context.row.original.resolution === 'Unresolved' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={(event) => {
              // The row itself is activatable; without this the click would open the dialog twice.
              event.stopPropagation()
              onResolve(context.row.original)
            }}
          >
            <Wrench />
            Resolve
          </Button>
        ) : (
          <span className="text-2xs text-muted">
            {context.row.original.resolvedAtUtc
              ? formatDateTime(context.row.original.resolvedAtUtc)
              : '—'}
          </span>
        ),
      meta: { widthClass: 'w-36' },
    }),
  ])
}

/**
 * Running a check on demand.
 *
 * Expensive — it reads every posting in the range — and safe to repeat. It is also exactly what a
 * day close runs on the way through, which makes running it before a close the way a branch finds
 * out what would block one while there is still time to do something about it.
 */
function RunDriftCheckCard({ defaultBranchId }: { defaultBranchId: string | null }) {
  const reach = useLedgerBranchReach()

  const [branchId, setBranchId] = useState<string | null>(defaultBranchId)
  const [fromDate, setFromDate] = useState(() => startOfMonth(today()))
  const [toDate, setToDate] = useState(today)
  const [result, setResult] = useState<LedgerDriftCheckResult | null>(null)

  const run = useRunDriftCheck()
  const rangeValid = fromDate !== '' && toDate !== '' && fromDate <= toDate

  return (
    <Card>
      <CardHeader>
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            <Play className="size-4 text-brand-text" aria-hidden />
            Run a check
          </CardTitle>
          <p className="mt-0.5 text-xs text-muted">
            Re-derives every stored rollup in the range from its postings. Safe to repeat, and worth
            running before a day close rather than discovering the alarms at the close itself.
          </p>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="check-branch">Branch</Label>
            <Select
              value={branchId ?? ANY}
              onValueChange={(next) => setBranchId(next === ANY ? null : next)}
            >
              <SelectTrigger id="check-branch">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Your own branch</SelectItem>
                {reach.branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branchDescription(branch)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="check-from">From</Label>
            <Input
              id="check-from"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="check-to">To</Label>
            <Input
              id="check-to"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>

          <div className="flex items-end">
            <Button
              variant="primary"
              disabled={!rangeValid || run.isPending}
              onClick={() => {
                run.mutate(
                  {
                    branchId: branchId === null ? null : Number(branchId),
                    fromDate,
                    toDate,
                  },
                  { onSuccess: setResult },
                )
              }}
            >
              {run.isPending ? 'Checking…' : 'Run the check'}
            </Button>
          </div>
        </div>

        {run.isError ? <ProblemAlert error={run.error} /> : null}

        {result ? <DriftCheckOutcome result={result} /> : null}
      </CardContent>
    </Card>
  )
}

/**
 * What a run found.
 *
 * `rollupsMissing` is called out separately from `rollupsDrifted` and given the stronger words: a
 * rollup that disagrees with its postings is wrong, while a rollup that is absent means a day's
 * postings were never summarised at all — nothing to be out by, and nothing for the trial balance
 * to read.
 */
function DriftCheckOutcome({ result }: { result: LedgerDriftCheckResult }) {
  const clean = result.rollupsDrifted === 0 && result.rollupsMissing === 0

  return (
    <Alert tone={clean ? 'success' : 'danger'}>
      {clean ? <Play aria-hidden /> : <TriangleAlert aria-hidden />}
      <div className="min-w-0 space-y-2">
        <AlertTitle>
          {clean
            ? 'Every rollup in that range follows from its postings'
            : 'The check found rollups that do not follow from their postings'}
        </AlertTitle>
        <AlertDescription>
          <span className="numeric tabular-nums">{result.rollupsChecked}</span> checked ·{' '}
          <span className="numeric tabular-nums">{result.rollupsDrifted}</span> drifted ·{' '}
          <span className="numeric tabular-nums">{result.rollupsRepaired}</span> repaired by the run ·{' '}
          <span className="numeric tabular-nums">{result.rollupsMissing}</span> missing.{' '}
          {result.rollupsMissing > 0 ? (
            <strong>
              A missing rollup is worse than a drifted one: a day&apos;s postings were never
              summarised, so there is no figure for the trial balance to read at all.
            </strong>
          ) : null}
        </AlertDescription>
        <p className="text-2xs text-muted">
          {DRIFT_TRIGGER_LABEL[result.trigger]} · {formatShortDate(result.fromDate)} to{' '}
          {formatShortDate(result.toDate)} · completed{' '}
          <span className="numeric tabular-nums">{formatDateTime(result.completedAtUtc)}</span> · run{' '}
          <span className="font-mono">{result.runPublicId}</span>
        </p>
        {result.alerts.length > 0 ? (
          <p className="text-xs text-body">
            <span className="numeric tabular-nums">{result.alerts.length}</span> alarm
            {result.alerts.length === 1 ? '' : 's'} raised, and now in the register below. A repaired
            rollup still raises one for the record — the repair is not the end of it, the reason is.
          </p>
        ) : null}
      </div>
    </Alert>
  )
}

/**
 * Disposing of an alarm.
 *
 * Two dispositions, and choosing between them is the whole decision:
 *
 * - **Repaired** — the stored rollup was wrong and has been put right from the postings.
 * - **Accepted with an explanation** — it has not been put right, and somebody is asserting the
 *   figure is nonetheless correct.
 *
 * The note is mandatory on both, and on the second it *is* the control. A difference the machine
 * cannot reconcile, signed off with no reasoning attached, is an audit finding rather than a
 * resolution — so the field is required, the dialog says why, and the button stays disabled until
 * something is written.
 *
 * `Unresolved` is not offered. It is the state an alarm is raised in, not a disposition anybody
 * chooses, and putting it in the list would let somebody "resolve" an alarm by re-asserting that it
 * is unresolved.
 */
function ResolveDriftDialog({
  alert,
  onClose,
}: {
  alert: LedgerBalanceDriftAlertRow
  onClose: () => void
}) {
  const permissions = useLedgerPermissions()
  const [resolution, setResolution] = useState<'Repaired' | 'AcceptedWithExplanation'>('Repaired')
  const [note, setNote] = useState('')
  const [resolved, setResolved] = useState<LedgerBalanceDriftAlertRow | null>(null)

  const resolve = useResolveDriftAlert()

  // Disposing has its own code now — `ledger.drift.resolve`, separate from viewing, because
  // accepting a difference with an explanation closes a control. A viewer without it reads the
  // figures and is told exactly which permission the disposition needs.
  const canDispose = permissions.canResolveDrift

  useEffect(() => {
    setResolution('Repaired')
    setNote('')
    setResolved(null)
  }, [alert.publicId])

  const trimmed = note.trim()

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          resolve.reset()
          onClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve this drift alarm</DialogTitle>
          <DialogDescription>
            {alert.subject} · <span className="font-mono">{alert.subjectKey}</span>
            {alert.businessDate ? <> · {formatShortDate(alert.businessDate)}</> : null}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <div className="grid grid-cols-3 gap-3 rounded-control border border-border-subtle bg-surface-muted p-3">
            <Figure label="Stored" value={alert.storedMinorUnits} />
            <Figure label="Derived" value={alert.derivedMinorUnits} />
            <div className="min-w-0">
              <p className="text-2xs font-semibold tracking-wide text-muted uppercase">Difference</p>
              <MoneyDisplay
                value={alert.differenceMinorUnits}
                signed
                colourNegative
                emphasis="strong"
                className="mt-0.5"
              />
            </div>
          </div>

          {resolved === null && !canDispose ? (
            <Alert tone="info">
              <TriangleAlert aria-hidden />
              <div className="space-y-1">
                <AlertTitle>Reading only</AlertTitle>
                <AlertDescription>
                  Disposing of an alarm needs{' '}
                  <span className="font-mono text-xs">{LEDGER_PERMISSIONS.driftResolve}</span>, which
                  your role does not hold. An unresolved alarm keeps blocking the day close until
                  somebody holding it records a disposition.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}

          {resolved === null && canDispose ? (
            <>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-heading">
                  What is being recorded about this alarm
                </legend>

                {(['Repaired', 'AcceptedWithExplanation'] as const).map((candidate) => (
                  <label
                    key={candidate}
                    className="flex cursor-pointer gap-3 rounded-control border border-border-subtle bg-surface p-3 max-lg:min-h-11"
                  >
                    <input
                      type="radio"
                      name="drift-resolution"
                      checked={resolution === candidate}
                      onChange={() => setResolution(candidate)}
                      className="mt-1"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-heading">
                        {DRIFT_RESOLUTION_LABEL[candidate]}
                      </span>
                      <span className="block text-xs text-body">
                        {DRIFT_RESOLUTION_MEANING[candidate]}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>

              <div className="space-y-1.5">
                <Label htmlFor="drift-note">Reason</Label>
                <textarea
                  id="drift-note"
                  rows={3}
                  autoFocus
                  value={note}
                  placeholder={
                    resolution === 'Repaired'
                      ? 'What was wrong with the stored figure, and what put it right.'
                      : 'Why the difference stands and why the figure is nonetheless correct.'
                  }
                  onChange={(event) => setNote(event.target.value)}
                  aria-describedby="drift-note-hint"
                  className="w-full rounded-control border border-border-subtle bg-surface px-3 py-2 text-sm text-heading outline-none transition-colors placeholder:text-muted focus:border-brand-text hover:border-border-strong"
                />
                <p id="drift-note-hint" className="text-xs text-muted">
                  Mandatory. It is stored on the alarm with your name and the time, and it is the
                  first thing read when this figure is questioned.
                </p>
              </div>

              {resolution === 'AcceptedWithExplanation' ? (
                <Alert tone="warning">
                  <TriangleAlert aria-hidden />
                  <div className="space-y-1">
                    <AlertTitle>You are signing off a figure that does not reconcile</AlertTitle>
                    <AlertDescription>
                      Accepting the difference clears the alarm and unblocks the day close without
                      changing the stored rollup. The trial balance will keep reporting the stored
                      figure — your explanation is the only account of why that is right.
                    </AlertDescription>
                  </div>
                </Alert>
              ) : null}

              {resolve.isError ? <ProblemAlert error={resolve.error} /> : null}
            </>
          ) : null}

          {resolved !== null ? (
            <Alert tone={resolved.resolution === 'Repaired' ? 'success' : 'warning'}>
              <Wrench aria-hidden />
              <div className="space-y-1">
                <AlertTitle>
                  Recorded as {DRIFT_RESOLUTION_LABEL[resolved.resolution].toLowerCase()}
                </AlertTitle>
                <AlertDescription>
                  The alarm no longer blocks the day close.
                  {resolved.resolvedAtUtc ? (
                    <>
                      {' '}
                      Recorded{' '}
                      <span className="numeric tabular-nums">
                        {formatDateTime(resolved.resolvedAtUtc)}
                      </span>
                      .
                    </>
                  ) : null}
                  {resolved.resolution === 'AcceptedWithExplanation'
                    ? ' The stored figure is unchanged and is still what the trial balance reports.'
                    : ''}
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              resolve.reset()
              onClose()
            }}
            disabled={resolve.isPending}
          >
            {resolved === null ? 'Cancel' : 'Close'}
          </Button>
          {resolved === null && canDispose ? (
            <Button
              variant={resolution === 'AcceptedWithExplanation' ? 'danger' : 'primary'}
              disabled={trimmed === '' || resolve.isPending}
              onClick={() => {
                resolve.mutate(
                  { alertId: alert.publicId, request: { resolution, note: trimmed } },
                  { onSuccess: setResolved },
                )
              }}
            >
              {resolve.isPending
                ? 'Working…'
                : resolution === 'Repaired'
                  ? 'Record the repair'
                  : 'Accept the difference'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</p>
      <MoneyDisplay value={value} className="mt-0.5" />
    </div>
  )
}
